import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

interface EmptyStateProps {
  /** Optional large muted icon above the title. */
  icon?: LucideIcon;
  title: string;
  /** One quiet explanatory line under the title. */
  hint?: ReactNode;
  /** Optional call-to-action rendered under the hint. */
  action?: ReactNode;
  /** 'card' wraps the state in a dashed rounded-xl container. */
  variant?: 'plain' | 'card';
}

/**
 * Shared empty state: centered icon + title + hint (+ optional action) —
 * the one recipe for "nothing here yet" across every tool, replacing the
 * per-module placeholder drift.
 */
export default function EmptyState({ icon: Icon, title, hint, action, variant = 'plain' }: EmptyStateProps) {
  const container =
    variant === 'card'
      ? 'flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600'
      : 'flex flex-col items-center justify-center py-12 text-center';
  return (
    <div className={container}>
      {Icon && <Icon className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />}
      <h3 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">{title}</h3>
      {hint && <div className="max-w-sm text-sm text-gray-500 dark:text-gray-400">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
